import { Typography } from '@mui/material'
import { Step } from 'react-joyride'
import { Link } from 'react-router-dom'
import type { Breakpoint } from '~hooks/use-breakpoint'
import { lastStep } from './common-steps'

const smallSizeTourBreakpoints: Breakpoint[] = ['sm', 'xs', 'md']
const mobileSizeTourBreakpoints: Breakpoint[] = ['xs', 'sm']

// Dashboard cards scroll internally, so scroll the card into view rather than
// an inner target. Otherwise Joyride measures the target against the card and
// scrolls the page to the top, leaving lower cards off screen on mobile.
const DOCUMENTS_SCROLL_TARGET = '.tour-step-dashboard-documents'
const KEY_FIGURES_SCROLL_TARGET = '.tour-step-dashboard-key-figures'
const UPDATES_SCROLL_TARGET = '.tour-step-dashboard-updates'
const BASECAMP_LINK_TARGET = '.tour-step-dashboard-basecamp-link'
const KEY_FIGURES_TITLE_TARGET = '.tour-step-dashboard-key-figures-title'
const UPDATES_TITLE_TARGET = '.tour-step-dashboard-updates-title'
const DOCUMENTS_TITLE_TARGET = '.tour-step-dashboard-documents-title'

// Spotlighting full-width panels on a phone forces the user to scroll to read
// the tooltip. Joyride skips both the spotlight and the scroll for `center`
// steps, while still dropping the step when its target is missing. Card titles
// and the Basecamp link are small enough to spotlight, so they stay pointed at.
const MOBILE_SPOTLIGHT_TARGETS = [
  BASECAMP_LINK_TARGET,
  KEY_FIGURES_TITLE_TARGET,
  UPDATES_TITLE_TARGET,
  DOCUMENTS_TITLE_TARGET,
]

function toCenteredStep(step: Step): Step {
  if (
    typeof step.target === 'string' &&
    MOBILE_SPOTLIGHT_TARGETS.includes(step.target)
  ) {
    return step
  }
  return { ...step, placement: 'center' }
}

export function constructDashboardTour({
  breakpoint,
  hasMultiplePools,
}: {
  breakpoint: Breakpoint
  hasMultiplePools?: boolean
}): Step[] {
  const isSmallSizeTour = smallSizeTourBreakpoints.includes(breakpoint)
  const isMobileSizeTour = mobileSizeTourBreakpoints.includes(breakpoint)
  // The Documents card is collapsed on load at mobile sizes, so its inner
  // targets are not rendered and these steps would be dropped.
  const documentsSubSteps: Step[] = [
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Find a document
          </Typography>
          <Typography variant="paragraph2" component="p">
            Search by document title to narrow the list down, and combine it
            with the Year and Category filters to find what you need.
          </Typography>
        </>
      ),
      target: '.tour-step-dashboard-documents-search',
      placement: isSmallSizeTour ? 'top' : 'left',
      scrollTarget: DOCUMENTS_SCROLL_TARGET,
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Bookmark a document
          </Typography>
          <Typography variant="paragraph2" component="p">
            Click the bookmark icon to mark a document so you can find it again
            quickly.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            The icon turns blue once the document is bookmarked, and clicking it
            again removes the bookmark.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            Turn on "Show only bookmarks" to list just your bookmarked
            documents.
          </Typography>
        </>
      ),
      target: '.tour-step-dashboard-documents-bookmark',
      placement: isSmallSizeTour ? 'top' : 'left',
      scrollTarget: DOCUMENTS_SCROLL_TARGET,
    },
  ]

  const steps: Step[] = [
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Welcome to your Dashboard
          </Typography>
          <Typography variant="paragraph2" component="p">
            The Dashboard gives you a quick overview of how your pool is
            performing and what has happened recently.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            You can skip the tour at any point, and you can always start it
            again from the <Link to="/help">Help page</Link>.
          </Typography>
        </>
      ),
      target: 'body',
      placement: 'center',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Key Figures
          </Typography>
          <Typography variant="paragraph2" component="p">
            Key Figures displays latest realised earnings and all forecasted
            earnings for the pool, month by month.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            Hover over a bar for a quick summary, or click it to see that
            month's details.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            Use the "Forecast" link for the full forecast breakdown.
          </Typography>
          {isMobileSizeTour && (
            <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
              Tap the heading to collapse or expand this section.
            </Typography>
          )}
        </>
      ),
      target: isMobileSizeTour
        ? KEY_FIGURES_TITLE_TARGET
        : KEY_FIGURES_SCROLL_TARGET,
      scrollTarget: KEY_FIGURES_SCROLL_TARGET,
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Updates
          </Typography>
          <Typography variant="paragraph2" component="p">
            Updates collects market comments, news and cargo activity in one
            feed. Each update is tagged so you can see what it is relevant to.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            Use the search field to look for words in an update, and the
            Category dropdown to show only one specific category of updates.
          </Typography>
        </>
      ),
      target: isMobileSizeTour ? UPDATES_TITLE_TARGET : UPDATES_SCROLL_TARGET,
      scrollTarget: UPDATES_SCROLL_TARGET,
      placement: isMobileSizeTour ? 'bottom' : 'top',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Historical data
          </Typography>
          <Typography variant="paragraph2" component="p">
            Older reports and data from before InSite are still available in
            Basecamp. This link, on Dashboard, opens it in a new tab.
          </Typography>
        </>
      ),
      target: BASECAMP_LINK_TARGET,
      placement: 'top',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Documents
          </Typography>
          <Typography variant="paragraph2" component="p">
            {isMobileSizeTour
              ? 'In this card you will find the pool documents shared with you.'
              : 'In the box to the right you will find the pool documents shared with you.'}
          </Typography>
          {!isMobileSizeTour && (
            <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
              Use the expand icon in the card header to view the full documents
              list, and the minimise icon to return to the Dashboard.
            </Typography>
          )}
          {isMobileSizeTour && (
            <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
              Tap the heading to open the card, then search and filter the list
              or bookmark a document to find it again quickly.
            </Typography>
          )}
        </>
      ),
      target: isMobileSizeTour
        ? DOCUMENTS_TITLE_TARGET
        : DOCUMENTS_SCROLL_TARGET,
      scrollTarget: DOCUMENTS_SCROLL_TARGET,
      placement: isMobileSizeTour ? 'bottom' : isSmallSizeTour ? 'top' : 'left',
    },
    ...(isMobileSizeTour ? [] : documentsSubSteps),
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Documents page
          </Typography>
          <Typography variant="paragraph2" component="p">
            The overview of documents is also available on the Documents page.
          </Typography>
        </>
      ),
      target: '.tour-step-nav-documents',
      placement: 'right',
    },
    lastStep,
  ]

  return isMobileSizeTour ? steps.map(toCenteredStep) : steps
}
